import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const frequencies = {
  'W': 'Weekly',
  'M': 'Monthly',
  'F': 'Fortnightly',
  'A': 'Annually',
  '4W': '4-Weekly',
  'Q': 'Quarterly',
};

const statementHeaders = {
  'income': 'Income',
  'fixed': 'Fixed Expenditure',
  'flexible': 'Flexible Expenditure',
  'debts': 'Additional Debt',
};

const hexToRgb = (hex) => {
  if (typeof hex !== 'string' || !hex.startsWith('#')) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  const sanitized = hex.replace(/^#/, '');
  if (sanitized.length !== 6) {
    throw new Error(`Hex color must be 6 characters: ${hex}`);
  }
  const bigint = parseInt(sanitized, 16);
  return [
    (bigint >> 16) & 255,
    (bigint >> 8) & 255,
    bigint & 255
  ];
};

const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(xhr.response);
    };
    xhr.onerror = reject;
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  });
};

const processData = (data) => {
  return Object.keys(data).map(key => {
    const name = data[key]["title"] || data[key]["key"];
    const freq = frequencies[data[key]["frequency"]] || "";
    const amount = data[key]["value"] || 0;
    return [name, freq, `£${amount.toFixed(2)}`];
  });
};

const generateFinancialStatement = async (
  financialData, context, config
) => {
  const {
    sfsIncome,
    sfsExpenditureFlexible,
    sfsAdditionalDebt,
    sfsExpenditureFixed,
    sfsTotals,
    disposableIncome
  } = financialData;

  const {
    party,
    account,
    clientLogo
  } = context;

  const { clientConfig } = config;

  const doc = new jsPDF();
  const statementTypes = ["income", "fixed", "flexible", "debts"];
  const info = 'This document is provided for reference and informational purposes only. It is not intended to serve as an official regulatory document. Please do not use this document for any formal financial or legal purposes.';
  const wrappedInfo = doc.splitTextToSize(info.trim(), 270);

  const palette = clientConfig?.theme?.palette;
  const primary = palette?.primary;
  const secondary = palette?.secondary;

  if (!primary?.main || !primary?.contrastText || !secondary?.main) {
    throw new Error("Missing required color values in clientConfig.theme.palette");
  }

  const branding = {
    logoBackgroundColor: hexToRgb(secondary.light),
    tableHeaderBackgroundColor: hexToRgb(primary.main),
    tableHeaderTextColor: hexToRgb(primary.contrastText)
  };

  const customerName = `${party?.customerName?.first ?? ''} ${party?.customerName?.last ?? ''}`.trim();
  let disposableText = '';

  if (typeof disposableIncome === 'number') {
    if (disposableIncome > 0) {
      disposableText = `+ £${disposableIncome.toFixed(2)}`;
    } else {
      disposableText = `- £${Math.abs(disposableIncome).toFixed(2)}`;
    }
  }

  const customerDetails = [
    ['Customer Name', customerName],
    ['Account Reference', account?.accountReference ?? ""],
    ['My Disposable Income (monthly)', disposableText],
    ['Date', new Date().toLocaleString().split(',')[0]],
  ];

  console.log("💡 Full palette dump:", JSON.stringify(clientConfig?.theme?.M?.palette?.M, null, 2));

  try {
    const logo = await loadImage(clientLogo);
    doc.setFillColor(...branding.logoBackgroundColor);
    doc.rect(0, 0, 250, 25, 'F');
    doc.addImage(logo, 'PNG', 10, 5, 65, 14);
  } catch (error) {
    console.error('Error loading logo:', error);
    doc.text('Company logo', 10, 10);
  }

  doc.setFontSize(11);
  doc.text(wrappedInfo, 10, 40);
  doc.autoTable({
    body: customerDetails,
    startY: 60,
    theme: 'striped',
    margin: { top: 20 },
    willDrawCell: (data) => {
      if (data.row.section === 'head' || data.cell.text === '') {
        data.cell.styles.fillColor = null;
        data.cell.text = '';
        data.cell.styles.lineWidth = 0;
      }
    },
  });

  statementTypes.forEach((type) => {
    let processedData = [];

    switch (type) {
      case "income":
        processedData = processData(sfsIncome);
        break;
      case "fixed":
        processedData = processData(sfsExpenditureFixed);
        break;
      case "flexible":
        processedData = processData(sfsExpenditureFlexible);
        break;
      case "debts":
        processedData = processData(sfsAdditionalDebt);
        break;
      default:
        return;
    }

    if (processedData.length === 0) return;

    const sectionTextPos = doc.autoTable.previous.finalY + 15;
    let totalCellPos = { label: 0, total: 0 };
    let continuingTable = false;
    let currentPage = doc.internal.getCurrentPageInfo().pageNumber;

    doc.setFontSize(14).setFont('helvetica', 'bold');
    doc.text(`My ${statementHeaders[type]}`, 10, sectionTextPos);

    doc.autoTable({
      head: [[`${statementHeaders[type]}`, 'Frequency', 'Amount']],
      body: processedData,
      startY: sectionTextPos + 5,
      theme: 'plain',
      headStyles: {
        fontStyle: 'bold',
        fillColor: branding.tableHeaderBackgroundColor,
        textColor: branding.tableHeaderTextColor
      },
      margin: { top: 20 },

      didDrawCell: (data) => {
        const y = data.cell.y + data.cell.height;

        totalCellPos.label = (data.cell.x - data.cell.padding('left')) - (data.column.width + data.cell.padding('left'));
        totalCellPos.total = data.cell.x + data.cell.padding('left');

        if (data.section === 'body') {
          doc.setLineWidth(0.2);
          doc.line(data.cell.x, y, data.cell.x + data.cell.width, y);
        }

        if (data.section === 'head' || (data.row.index === 0 && continuingTable)) {
          data.cell.text = ["", "", ""];
          data.cell.styles = { fillColor: null, textColor: null, lineWidth: 0 };
        }
      },

      didDrawPage: () => {
        const newPage = doc.internal.getCurrentPageInfo().pageNumber;
        if (newPage !== currentPage) {
          continuingTable = true;
          currentPage = newPage;
          doc.setFontSize(10).setFont('helvetica', 'italic');
          doc.text(`My ${statementHeaders[type]} (continued)`, 10, 15);
        }
      },
    });

    const finalY = doc.autoTable.previous.finalY;
    const total = sfsTotals?.totals.find(item => item.name === statementHeaders[type])?.value;

    doc.setFontSize(12).setFont('helvetica', 'bold');
    doc.text('Total:', totalCellPos.label, finalY + 5);
    doc.text(`£${total?.toFixed(2) ?? '0.00'}`, totalCellPos.total, finalY + 5);
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() - 20, doc.internal.pageSize.getHeight() - 10);
  }

  doc.save('financial-statement.pdf');
};

export default generateFinancialStatement;
